import { useEffect, useRef } from 'react';
import { addDays, toDateString, isToday, formatKoreanDate } from '@/lib/dates';
import type { CalendarEvent } from '@/types';
import styles from './DayView.module.css';

const DAYS_BEFORE = 7;
const DAYS_TOTAL  = 180;
const COLOR_MAP: Record<string, string> = {
  blue:   'var(--event-blue)',
  purple: 'var(--event-purple)',
  green:  'var(--event-green)',
  orange: 'var(--event-orange)',
  pink:   'var(--event-pink)',
  cyan:   'var(--event-cyan)',
};

interface Props {
  selectedDate: Date;
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  onDateChange?: (date: Date) => void;
}

export function DayView({ selectedDate, events, onEventClick, onDateChange }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrolledKey = useRef('');
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 마운트 시 한 번만 계산
  const baseDateRef = useRef<Date | null>(null);
  const allDaysRef = useRef<Date[]>([]);
  if (!baseDateRef.current) {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    baseDateRef.current = addDays(d, -DAYS_BEFORE);
    allDaysRef.current = Array.from({ length: DAYS_TOTAL }, (_, i) => addDays(baseDateRef.current!, i));
  }

  function pageW() { return scrollRef.current?.offsetWidth ?? 0; }

  function scrollToDate(date: Date, behavior: ScrollBehavior) {
    if (!scrollRef.current) return;
    const diff = Math.round((date.getTime() - baseDateRef.current!.getTime()) / 86400000);
    scrollRef.current.scrollTo({ left: Math.max(0, diff) * pageW(), behavior });
  }

  useEffect(() => {
    scrollToDate(selectedDate, 'instant');
    lastScrolledKey.current = toDateString(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const key = toDateString(selectedDate);
    if (key === lastScrolledKey.current) return;
    lastScrolledKey.current = key;
    scrollToDate(selectedDate, 'smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  function handleScroll() {
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      if (!scrollRef.current) return;
      const idx = Math.round(scrollRef.current.scrollLeft / pageW());
      const clamped = Math.max(0, Math.min(idx, allDaysRef.current.length - 1));
      const date = allDaysRef.current[clamped];
      if (date) {
        lastScrolledKey.current = toDateString(date);
        onDateChange?.(date);
      }
    }, 80);
  }

  return (
    <div className={styles.dayView}>
      <div ref={scrollRef} className={styles.scrollContainer} onScroll={handleScroll}>
        {allDaysRef.current.map((day, i) => {
          const ds = toDateString(day);
          const dayEvents = events.filter(e => e.date === ds);
          const allDay = dayEvents.filter(e => e.isAllDay || !e.startTime);
          const timed  = dayEvents
            .filter(e => !e.isAllDay && e.startTime)
            .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
          const isTodayDay = isToday(day);

          return (
            <div key={i} className={styles.dayPage}>
              {/* 날짜 헤더 */}
              <div className={`${styles.dayHeader} ${isTodayDay ? styles.todayHeader : ''}`}>
                <span className={styles.dateLabel}>{formatKoreanDate(day)}</span>
                {isTodayDay && <span className={styles.todayBadge}>오늘</span>}
              </div>

              {/* 이벤트 리스트 */}
              <div className={styles.eventList}>
                {/* 종일 이벤트 */}
                {allDay.map(e => (
                  <button
                    key={e.id}
                    className={styles.eventCard}
                    style={{ borderLeft: `4px solid ${COLOR_MAP[e.color] || COLOR_MAP.blue}` }}
                    onClick={() => onEventClick(e)}
                  >
                    <div className={styles.cardMeta}>
                      <span className={styles.cardAllDayBadge}>종일</span>
                    </div>
                    <span className={styles.cardTitle}>{e.title}</span>
                    {e.description && (
                      <span className={styles.cardDesc}>{e.description}</span>
                    )}
                  </button>
                ))}

                {/* 시간 이벤트 */}
                {timed.map(e => (
                  <button
                    key={e.id}
                    className={styles.eventCard}
                    style={{ borderLeft: `4px solid ${COLOR_MAP[e.color] || COLOR_MAP.blue}` }}
                    onClick={() => onEventClick(e)}
                  >
                    <div className={styles.cardMeta}>
                      <span className={styles.cardTime}>
                        {e.startTime}{e.endTime ? ` – ${e.endTime}` : ''}
                      </span>
                    </div>
                    <span className={styles.cardTitle}>{e.title}</span>
                    {e.description && (
                      <span className={styles.cardDesc}>{e.description}</span>
                    )}
                  </button>
                ))}

                {/* 일정 없음 */}
                {dayEvents.length === 0 && (
                  <div className={styles.empty}>
                    <span className={styles.emptyIcon}>○</span>
                    <span className={styles.emptyText}>일정 없음</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
